import { useCallback, useEffect, useRef, useState } from 'react'
import useTranslation from '@/hooks/useTranslation'
import { LANGUAGE_NAMES } from '@/locales/languages'
import { Emoji } from 'react-apple-emojis'
import { ChevronSelectorVertical } from '@untitledui/icons'
import { AnimatePresence, motion } from 'motion/react'

import { Drawer } from 'vaul'

const FLAG_EMOJIS = {
    fr: 'flag-france',
    en: 'flag-united-kingdom',
    ja: 'flag-japan',
    de: 'flag-germany',
    no: 'flag-norway',
    tr: 'flag-turkey',
    zh: 'flag-china',
    es: 'flag-spain',
    th: 'flag-thailand',
    cs: 'flag-czechia',
    is: 'flag-iceland',
}

function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(
        () => window.matchMedia('(min-width: 1024px)').matches
    )

    useEffect(() => {
        const mql = window.matchMedia('(min-width: 1024px)')
        const handler = (e) => setIsDesktop(e.matches)
        mql.addEventListener('change', handler)
        return () => mql.removeEventListener('change', handler)
    }, [])

    return isDesktop
}

function usePopoverPosition(ref, isOpen) {
    const [placement, setPlacement] = useState('top')
    const [maxHeight, setMaxHeight] = useState(300)

    const calculate = useCallback(() => {
        if (!ref.current || !isOpen) return

        const rect = ref.current.getBoundingClientRect()
        const spaceAbove = rect.top
        const spaceBelow = window.innerHeight - rect.bottom
        const popoverHeight = 300
        const margin = 16

        if (spaceAbove >= popoverHeight + margin) {
            setPlacement('top')
            setMaxHeight(Math.min(popoverHeight, spaceAbove - margin))
        } else if (spaceBelow >= popoverHeight + margin) {
            setPlacement('bottom')
            setMaxHeight(Math.min(popoverHeight, spaceBelow - margin))
        } else if (spaceAbove > spaceBelow) {
            setPlacement('top')
            setMaxHeight(spaceAbove - margin)
        } else {
            setPlacement('bottom')
            setMaxHeight(spaceBelow - margin)
        }
    }, [ref, isOpen])

    useEffect(() => {
        calculate()
    }, [calculate])

    return { placement, maxHeight }
}

function DesktopSelector({ language, setLanguage, fullWidth }) {
    const [isOpen, setIsOpen] = useState(false)
    const ref = useRef(null)
    const { placement, maxHeight } = usePopoverPosition(ref, isOpen)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const popoverClasses =
        placement === 'top' ? 'bottom-full left-0 mb-2' : 'top-full left-0 mt-2'

    const animationY = placement === 'top' ? 4 : -4

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative flex h-10 shrink-0 items-center justify-center gap-[8px] overflow-hidden px-4 text-sm font-medium transition-all cursor-pointer border border-dark-blue-10 rounded-[8px] ${
                    fullWidth ? 'w-full' : 'w-[132px]'
                }`}
            >
                <Emoji name={FLAG_EMOJIS[language]} width={16} />
                <span className="text-[14px] text-dark-blue-40 font-semibold">
                    {LANGUAGE_NAMES[language]}
                </span>

                <div className="ml-auto">
                    <ChevronSelectorVertical
                        size={16}
                        className="text-dark-blue-40 ml-1 shrink-0"
                    />
                </div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: animationY }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: animationY }}
                        transition={{ duration: 0.15 }}
                        className={`absolute ${popoverClasses} bg-white border border-dark-blue-10 rounded-lg p-[4px] min-w-full overflow-y-auto flex flex-col gap-[4px]`}
                        style={{ maxHeight }}
                    >
                        {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                            <button
                                key={code}
                                onClick={() => {
                                    setLanguage(code)
                                    setIsOpen(false)
                                }}
                                className={`text-[14px] font-semibold text-dark-blue-40 flex items-center gap-[8px] hover:bg-gray-50 w-full cursor-pointer px-3 py-2 rounded-[4px] ${
                                    code === language
                                        ? 'bg-background-product'
                                        : ''
                                }`}
                            >
                                <Emoji name={FLAG_EMOJIS[code]} width={14} />
                                <span>{name}</span>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

function MobileSelector({ language, setLanguage }) {
    const [isOpen, setIsOpen] = useState(false)

    const handleLanguageChange = (langCode) => {
        setLanguage(langCode)
        setIsOpen(false)
    }

    return (
        <Drawer.Root
            open={isOpen}
            onOpenChange={setIsOpen}
            container={document.getElementById('vaul-portal')}
        >
            <Drawer.Trigger className="relative flex h-10 shrink-0 items-center justify-center gap-[8px] overflow-hidden px-4 text-sm font-medium transition-all cursor-pointer">
                <Emoji name={FLAG_EMOJIS[language]} width={16} />
                <span className="text-[14px] text-gray-400 font-semibold">
                    {LANGUAGE_NAMES[language]}
                </span>
                <ChevronSelectorVertical
                    size={16}
                    className="text-gray-400 ml-1"
                />
            </Drawer.Trigger>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 bg-black/40" />
                <Drawer.Content className="bg-gray-100 flex flex-col rounded-t-[10px] mt-24 h-[38%] fixed bottom-0 left-0 right-0 outline-none">
                    <div className="p-4 bg-white rounded-t-[10px] flex-1 flex flex-col overflow-y-auto">
                        <div
                            aria-hidden
                            className="mx-auto w-12 h-1.5 shrink-0 rounded-full bg-gray-300 mb-8"
                        />
                        <div className="flex flex-col overflow-y-auto">
                            {Object.entries(LANGUAGE_NAMES).map(
                                ([code, name]) => (
                                    <div key={code}>
                                        <button
                                            onClick={() =>
                                                handleLanguageChange(code)
                                            }
                                            className={`text-[16px] flex items-center gap-[12px] hover:bg-gray-50 w-full cursor-pointer p-[12px] rounded-lg ${
                                                code === language
                                                    ? 'bg-gray-100'
                                                    : ''
                                            }`}
                                        >
                                            <Emoji
                                                name={FLAG_EMOJIS[code]}
                                                width={32}
                                            />

                                            <span className="font-semibold text-gray-400">
                                                {name}
                                            </span>
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    )
}

export default function LanguageSelector({ fullWidth = false }) {
    const { language, setLanguage } = useTranslation()
    const isDesktop = useIsDesktop()

    if (isDesktop) {
        return (
            <DesktopSelector
                language={language}
                setLanguage={setLanguage}
                fullWidth={fullWidth}
            />
        )
    }

    return <MobileSelector language={language} setLanguage={setLanguage} />
}
